import { Component } from '@theme/component';

/**
 * Return a calendar date in Canberra, independent of the device timezone.
 * UTC arithmetic is used only for adding calendar days, avoiding DST-length days.
 * @param {unknown} leadTime
 * @param {Date} now
 * @returns {string}
 */
export function earliestFulfilmentDate(leadTime, now = new Date()) {
  const raw = typeof leadTime === 'string' ? leadTime.trim() : leadTime;
  const number = typeof raw === 'number' || (typeof raw === 'string' && /^\d+$/.test(raw))
    ? Number(raw) : 0;
  const days = Number.isSafeInteger(number) && number >= 0 ? number : 0;
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Sydney',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', hourCycle: 'h23',
  }).formatToParts(now).map(({ type, value }) => [type, value]));
  const base = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day));
  const cutoffDays = Number(parts.hour) >= 15 ? 1 : 0;
  const result = new Date(base);
  result.setUTCDate(result.getUTCDate() + days + cutoffDays);
  // Out-of-range dates cannot be represented by the native date input.
  if (!Number.isFinite(result.getTime()) || result.getUTCFullYear() > 9999) {
    return new Date(base + cutoffDays * 86400000).toISOString().slice(0, 10);
  }
  return result.toISOString().slice(0, 10);
}

/** Parse merchant-entered ISO calendar dates; reject impossible dates and duplicates. */
function parseHolidayDates(raw = '') {
  return new Set(String(raw).split(/\r?\n/).map(line => line.trim()).filter(value => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || value.startsWith('0000')) return false;
    const date = new Date(value + 'T00:00:00Z');
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
  }));
}

/** Move by calendar days without applying a business-day or weekend calendar. */
function shiftDate(value, days) {
  const date = new Date(value + 'T00:00:00Z');
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** Return an empty string when bookable, otherwise a machine-readable reason. */
function holidayDateError(value, method, leadTime, holidays, now = new Date()) {
  if (!value) return '';
  if (method === 'Pickup' && holidays.has(value)) return 'pickup_holiday';
  if (value < earliestFulfilmentDate(leadTime, now)) return 'date_unavailable';
  if (method !== 'Delivery' || !holidays.has(value)) return '';
  let deadlineDay = shiftDate(value, -1);
  while (holidays.has(deadlineDay)) deadlineDay = shiftDate(deadlineDay, -1);
  // With lead time zero, the effective order date moves forward at exactly 3 PM.
  // It exceeds the deadline day at the deadline itself, or on any later day.
  return earliestFulfilmentDate(0, now) > deadlineDay ? 'delivery_holiday_closed' : '';
}

// TODO: Public-holiday delivery surcharge is handled separately from theme date availability.

/** Return bookable slot starts, after applying the unchanged date rules. */
function availableTimeSlots(date, method, leadTime, holidays, now = new Date()) {
  if (!date || !parseHolidayDates(date).has(date) || holidayDateError(date, method, leadTime, holidays, now)) return [];
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(now).map(({ type, value }) => [type, value]));
  const today = parts.year + '-' + parts.month + '-' + parts.day;
  const minutes = Number(parts.hour) * 60 + Number(parts.minute);
  const starts = method === 'Pickup' ? [10, 12, 14, 16] : [10, 12, 14, 16, 18];
  const sundayPickup = method === 'Pickup' && new Date(date + 'T00:00:00Z').getUTCDay() === 0;
  return starts.filter(hour => (!sundayPickup || hour >= 12) && (date > today || (date === today && hour * 60 > minutes)));
}

/** Product-page line item fields, using Horizon's declarative event lifecycle. */
class HoppipollaOrderFields extends Component {
  #dateTimer;
  #dateListeners;
  #method;

  connectedCallback() {
    super.connectedCallback();
    this.syncFields();
    this.#startDateUpdates();
  }

  updatedCallback() {
    super.updatedCallback();
    this.syncFields();
    this.refreshDate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearInterval(this.#dateTimer);
    this.#dateListeners?.abort();
  }

  /** Preserve the customer's selection; native validation flags an expired date. */
  refreshDate() {
    const input = this.querySelector('[data-fulfilment-date]');
    if (!(input instanceof HTMLInputElement)) return;
    const now = new Date();
    const method = this.querySelector('[data-method]')?.value || 'Delivery';
    const holidays = parseHolidayDates(this.dataset.holidayDates);
    let minimum = earliestFulfilmentDate(this.dataset.leadTimeDays, now);
    while (holidayDateError(minimum, method, this.dataset.leadTimeDays, holidays, now)) {
      minimum = shiftDate(minimum, 1);
    }
    input.min = minimum;
    const reason = holidayDateError(input.value, method, this.dataset.leadTimeDays, holidays, now);
    const message = this.#dateMessage(reason);
    input.setCustomValidity(message);
    const notice = this.querySelector('[data-holiday-notice]');
    if (notice) notice.hidden = !(input.value && !reason && method === 'Delivery' && holidays.has(input.value));
    if (input.value) this.#showDateMessage(message);
    this.refreshTimeSlots(now);
    return reason;
  }

  /** Rebuild only when the available options change; preserve a still-valid selection. */
  refreshTimeSlots(now = new Date()) {
    const select = this.querySelector('[data-fulfilment-time]');
    if (!(select instanceof HTMLSelectElement)) return;
    const date = this.querySelector('[data-fulfilment-date]')?.value || '';
    const method = this.querySelector('[data-method]')?.value || 'Delivery';
    const slots = availableTimeSlots(date, method, this.dataset.leadTimeDays, parseHolidayDates(this.dataset.holidayDates), now);
    const previous = select.value;
    const valid = slots.some(hour => this.#slotValue(hour) === previous);
    const key = slots.join(',');
    if (select.dataset.slots !== key) {
      select.replaceChildren(new Option(slots.length ? this.dataset.selectTimeMessage : this.dataset.selectDateMessage, ''));
      for (const hour of slots) {
        select.add(new Option(this.dataset['slot' + hour], this.#slotValue(hour)));
      }
      select.dataset.slots = key;
    }
    select.disabled = !slots.length;
    select.required = Boolean(slots.length);
    select.setAttribute('aria-required', String(select.required));
    select.value = valid ? previous : '';
    const error = this.querySelector('[data-time-error]');
    if (error) {
      if (previous && !valid) {
        error.textContent = this.dataset.timeExpiredMessage;
        error.hidden = false;
      } else if (select.value) {
        error.hidden = true;
      }
    }
  }

  #slotValue(hour) {
    const end = hour === 18 ? 19 : hour + 2;
    return String(hour).padStart(2, '0') + ':00–' + String(end).padStart(2, '0') + ':00';
  }

  /** Selecting a replacement slot dismisses its stale-selection message. */
  validateTimeSelection() {
    this.refreshDate();
  }

  #dateMessage(reason) {
    return {
      pickup_holiday: this.dataset.pickupHolidayMessage,
      delivery_holiday_closed: this.dataset.deliveryHolidayMessage,
      date_unavailable: this.dataset.dateUnavailableMessage,
    }[reason] || '';
  }

  #showDateMessage(message) {
    const error = this.querySelector('[data-date-error]');
    if (error) {
      if (error.textContent !== message) error.textContent = message;
      error.hidden = !message;
    }
  }

  /** Reject an unavailable selection immediately, preserving all other form data. */
  validateDateSelection() {
    const reason = this.refreshDate();
    const input = this.querySelector('[data-fulfilment-date]');
    if (!(input instanceof HTMLInputElement)) return;
    if (reason) {
      input.value = '';
      input.setCustomValidity('');
      const notice = this.querySelector('[data-holiday-notice]');
      if (notice) notice.hidden = true;
      this.#showDateMessage(this.#dateMessage(reason));
    } else {
      this.#showDateMessage('');
    }
    this.refreshTimeSlots();
  }

  #startDateUpdates() {
    clearInterval(this.#dateTimer);
    this.#dateListeners?.abort();
    const input = this.querySelector('[data-fulfilment-date]');
    if (!(input instanceof HTMLInputElement)) return;
    this.refreshDate();
    this.#dateListeners = new AbortController();
    const options = { signal: this.#dateListeners.signal, capture: true };
    // Native required-field bubbles otherwise follow the browser UI language, not the storefront locale.
    document.addEventListener('invalid', (event) => {
      const control = event.target;
      if (control?.form === input.form && control.validity?.valueMissing && this.dataset.requiredFieldMessage) {
        control.setCustomValidity(this.dataset.requiredFieldMessage);
      }
    }, options);
    document.addEventListener('input', (event) => {
      const control = event.target;
      if (control?.form === input.form && control.validationMessage === this.dataset.requiredFieldMessage) {
        control.setCustomValidity('');
      }
    }, options);
    // Refresh before native submit validation, including after a background tab resumes.
    document.addEventListener('click', () => this.refreshDate(), options);
    document.addEventListener('focusin', () => this.refreshDate(), options);
    document.addEventListener('visibilitychange', () => this.refreshDate(), options);
    document.addEventListener('submit', (event) => {
      if (event.target !== input.form) return;
      this.refreshDate();
      this.updateCount();
      const time = this.querySelector('[data-fulfilment-time]');
      const timeValid = !(time instanceof HTMLSelectElement) || (!time.disabled && time.reportValidity());
      if (!input.reportValidity() || !timeValid || input.form.reportValidity?.() === false) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, options);
    this.#dateTimer = setInterval(() => this.refreshDate(), 60000);
  }

  /** Synchronise conditional controls without discarding the customer's draft input. */
  syncFields() {
    const card = this.querySelector('[data-card]');
    const messageGroup = this.querySelector('[data-message-group]');
    const message = this.querySelector('[data-message]');
    if (card && messageGroup && message) {
      const hasCard = card.value !== 'No Card';
      messageGroup.hidden = !hasCard;
      message.disabled = !hasCard;
      message.required = hasCard;
      message.setAttribute('aria-required', String(hasCard));
    }

    const method = this.querySelector('[data-method]');
    if (method && this.#method !== method.value) {
      this.#method = method.value;
      this.validateDateSelection();
    }
    const delivery = this.querySelector('[data-delivery]');
    if (method && delivery) {
      const isDelivery = method.value === 'Delivery';
      delivery.hidden = !isDelivery;
      for (const input of delivery.querySelectorAll('input, select, textarea')) {
        input.disabled = !isDelivery;
        // Operational notes only. Checkout is authoritative for the final method/recipient.
        // Disabled Pickup controls retain drafts locally but cannot submit stale properties.
        input.required = false;
        input.setAttribute('aria-required', String(input.required));
      }
    }
    this.updateCount();
  }

  /** Update the visible, translated character counter. */
  updateCount() {
    const message = this.querySelector('[data-message]');
    const count = this.querySelector('[data-count]');
    if (message && count) {
      message.setCustomValidity(!message.disabled && message.value.length > 250 ? this.dataset.messageTooLong : '');
      count.textContent = count.dataset.template.replace('[count]', String(message.value.length));
    }
  }
}

if (!customElements.get('hoppipolla-order-fields')) {
  customElements.define('hoppipolla-order-fields', HoppipollaOrderFields);
}
